/**
 * Auflösung eines geteilten Dokument-Links (#516).
 *
 * Sitzt unter `/d/:documentId` innerhalb von `ProtectedRoute`, aber AUSSERHALB
 * beider `RequireApplication`-Gates: welcher Wirt zuständig ist, entscheidet
 * genau diese Seite — ein Gate davor würde einen Teil der Empfänger abweisen,
 * bevor die Frage überhaupt gestellt ist.
 *
 * Warum eine eigene Route und nicht zwei Einstiegspunkte: die Meldung „du
 * kannst dieses Dokument nicht öffnen" braucht GENAU EINE Implementierung. In
 * beide Wirte gebaut driftet sie auseinander, und „jemand hat mir einen Link
 * geschickt, auf den ich keinen Zugriff habe" ist der wahrscheinlichste Verlauf
 * überhaupt für dieses Feature. Außerdem bleibt der geteilte Link stabil, wenn
 * später ein dritter Wirt dazukommt.
 *
 * Der Zugriff wird hier nicht selbst entschieden, sondern erfragt: `GET
 * /documents/{id}` läuft im Backend durch `check_document_access`. Dieselbe
 * Antwort liefert nebenbei die Knowledge Area, die der Wirt braucht, um das
 * Dokument im richtigen Zweig zu zeigen — ein Request für beides.
 */
import { useEffect, useState } from 'react'
import { Navigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { FileQuestion, Loader2, Lock } from 'lucide-react'
import { cdbTqsApiService } from '@/services/cdb-tqs-api-service'
import { CdbTqsApiError } from '@/services/cdb-tqs/http'
import { useAllowedApplications } from '@/hooks/useAllowedApplications'
import { documentTargetFor } from '@/lib/document-link'
import { logError, logInfo } from '@/lib/logger'

type Resolution =
  | { state: 'loading' }
  /** Zugriff bestätigt — Ziel steht fest. */
  | { state: 'ready'; knowledgeArea?: string }
  /** Angemeldet, aber dieses Dokument darf der Nutzer nicht sehen (403/404). */
  | { state: 'denied' }
  /** Backend nicht erreichbar o. ä. — ausdrücklich NICHT als "kein Zugriff" zeigen. */
  | { state: 'error' }

/**
 * 403 und 404 laufen zusammen: Für einen Nutzer ohne Zugriff darf nicht
 * erkennbar sein, ob es das Dokument überhaupt gibt. Beides heißt hier „du
 * kannst dieses Dokument nicht öffnen".
 *
 * 400/422 gehören dazu: `document_id` ist serverseitig eine UUID, ein
 * abgeschnittener oder verstümmelter Link wird also abgewiesen, bevor irgendein
 * Dokument gesucht wird. Das als „Backend nicht erreichbar" zu melden wäre
 * schlicht falsch — das Backend hat geantwortet. Ein Orakel entsteht dadurch
 * nicht: eine kaputte UUID benennt kein Dokument, dessen Existenz sie verraten
 * könnte.
 */
function isNoAccess(error: unknown): boolean {
  return (
    error instanceof CdbTqsApiError &&
    (error.status === 403 || error.status === 404 || error.status === 400 || error.status === 422)
  )
}

export function DocumentLinkPage() {
  const { documentId } = useParams<{ documentId: string }>()
  const { apps, isLoading: isLoadingApps } = useAllowedApplications()
  const { t } = useTranslation()
  const [resolution, setResolution] = useState<Resolution>({ state: 'loading' })

  useEffect(() => {
    if (!documentId) {
      setResolution({ state: 'denied' })
      return
    }

    let active = true

    void (async () => {
      try {
        const doc = await cdbTqsApiService.getDocument(documentId)
        if (!active) return
        setResolution({ state: 'ready', knowledgeArea: doc?.knowledge_area })
      } catch (error) {
        if (!active) return
        if (isNoAccess(error)) {
          logInfo({ context: 'DocumentLinkPage', documentId }, 'Shared document not accessible')
          setResolution({ state: 'denied' })
          return
        }
        logError(error as Error, { context: 'DocumentLinkPage', documentId })
        setResolution({ state: 'error' })
      }
    })()

    return () => {
      active = false
    }
  }, [documentId])

  if (isLoadingApps || resolution.state === 'loading') {
    return <LinkState icon="spinner" title={t('documentLink.loading')} />
  }

  if (resolution.state === 'denied') {
    return (
      <LinkState
        icon="lock"
        title={t('documentLink.denied.title')}
        message={t('documentLink.denied.message')}
      />
    )
  }

  if (resolution.state === 'error') {
    return (
      <LinkState
        icon="question"
        title={t('documentLink.error.title')}
        message={t('documentLink.error.message')}
      />
    )
  }

  const target = documentTargetFor(documentId!, apps ?? [], resolution.knowledgeArea)

  if (!target) {
    return (
      <LinkState
        icon="lock"
        title={t('documentLink.noHost.title')}
        message={t('documentLink.noHost.message')}
      />
    )
  }

  // `replace`: Der Resolver ist eine Weiche, keine Station. Bliebe er in der
  // History, führte der Zurück-Knopf aus dem Dokument wieder in die Weiche und
  // von dort sofort zurück ins Dokument.
  return <Navigate to={target} replace />
}

function LinkState({
  icon,
  title,
  message,
}: {
  icon: 'spinner' | 'lock' | 'question'
  title: string
  message?: string
}) {
  return (
    <div className="app-surface flex h-full min-h-screen w-full flex-col items-center justify-center gap-3 p-8 text-center">
      {icon === 'spinner' && (
        <Loader2 className="h-7 w-7 animate-spin motion-reduce:animate-none text-[var(--text-secondary)]" />
      )}
      {icon === 'lock' && <Lock className="h-7 w-7 text-[var(--text-secondary)]" />}
      {icon === 'question' && <FileQuestion className="h-7 w-7 text-[var(--text-secondary)]" />}
      <h1 className="text-lg font-medium text-[var(--text-color)]">{title}</h1>
      {message && <p className="max-w-md text-sm text-[var(--text-secondary)]">{message}</p>}
    </div>
  )
}
